import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { Formik } from 'formik'
import { Gear, Info, LockKey } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import API from '../../../../../api/api'
import { getVoicemailMessages, selectVoicemailMessages } from '../../../../../reducers/chatReducer'
import Filter from '../../../../Filter/Filter'
import SelectField from '../../../../FormLegacy/FormFieldsV2/SelectField'
import CheckboxField from '../../../../FormLegacy/FormFieldsV2/CheckboxField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import CallSettingsUpselling from '../../CallSettingsUpselling/CallSettingsUpselling'
import ManageVoicemailWindow from '../../ManageVoicemailWindow/ManageVoicemailWindow'
import { getRingDurationOptions } from '../../helpers'
import FormikAutoSubmit from '../../../../../helpers/formikAutoSubmit'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'

const CallingSettings = ({
  building,
  inbox,
  voicemailMessages,
  getVoicemailMessages = () => {},
}) => {
  const { t } = useTranslation(['chat'])
  const { displaySnackbar } = useSnackbar()

  const ringDurationOptions = getRingDurationOptions(t)

  const isMaintenanceCallAvailable = building && building.maintenanceCallAvailable

  const [upsellingVisible, setUpsellingVisible] = useState(false)
  const [isManageVoicemailVisible, setManageVoicemailVisible] = useState(false)
  const [isVoicemailLoading, setVoicemailLoading] = useState(false)

  const voicemailMessageOptions = voicemailMessages.map((message) => ({
    id: message.id,
    text: message.name,
    value: message.id,
  }))

  const onCallSettingsSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true)
      await API.updateChatInboxes(inbox.id, values)
    } catch ({ message }) {
      displaySnackbar(message, true)
    } finally {
      setSubmitting(false)
    }
  }

  const fetchVoicemailMessages = async (inboxId) => {
    try {
      setVoicemailLoading(true)
      await getVoicemailMessages(inboxId)
    } catch ({ message }) {
      displaySnackbar(t('chat:callingSettings.loadFailure', { message }), true)
    } finally {
      setVoicemailLoading(false)
    }
  }

  const getFilterPlaceholder = (voicemailId) => {
    const voicemailIndex = voicemailMessageOptions.findIndex((el) => el.value === voicemailId)
    return voicemailIndex !== -1 && voicemailMessageOptions.length
      ? voicemailMessageOptions[voicemailIndex].text
      : t('chat:callingSettings.selectVoicemailGreeting')
  }

  const getFilterValue = (voicemailId) => {
    const voiceMailIndex = voicemailMessageOptions.findIndex((el) => el.value === voicemailId)
    return voiceMailIndex !== -1 && voicemailMessageOptions.length
      ? [voicemailMessageOptions[voiceMailIndex]]
      : []
  }

  useEffect(() => {
    if (inbox.id) {
      fetchVoicemailMessages(inbox.id)
    }
  }, [inbox])

  return (
    <React.Fragment>
      <div className="calling-settings">
        <div className="section-header">
          <span>{t('chat:callingSettings.callSettings')}</span>
          {!isMaintenanceCallAvailable ? (
            <div className="locked">
              <LockKey /> {t('chat:callingSettings.locked')}{' '}
              <span onClick={() => setUpsellingVisible(true)}>
                {t('chat:callingSettings.moreInfo')}
              </span>
            </div>
          ) : null}
        </div>
        {isMaintenanceCallAvailable ? (
          <Formik
            enableReinitialize={true}
            initialValues={{
              recordCalls: inbox.recordCalls,
              callTranscription: inbox.callTranscription,
              voicemailEnabled: inbox.voicemailEnabled,
              voicemailMessage:
                inbox.voicemailEnabled && inbox.voicemailMessage ? inbox.voicemailMessage.id : null,
              ringDuration: inbox.ringDuration,
              outOfOfficeVoicemailMessage:
                inbox.voicemailEnabled && inbox.outOfOfficeVoicemailMessage
                  ? inbox.outOfOfficeVoicemailMessage.id
                  : null,
              outOfOfficeRingDuration: inbox.outOfOfficeRingDuration,
              outOfOfficeInstantVoicemail: inbox.outOfOfficeInstantVoicemail,
            }}
            onSubmit={onCallSettingsSubmit}
          >
            {({ handleChange, setFieldValue, handleSubmit, values, initialValues }) => (
              <React.Fragment>
                <FormikAutoSubmit
                  submitForm={handleSubmit}
                  values={values}
                  initialValues={initialValues}
                />
                <div className="flex-row">
                  <div className="flex-col toggles-container">
                    <ToggleField
                      name="recordCalls"
                      value={values.recordCalls}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          setFieldValue('callTranscription', e.target.checked)
                        }
                        setFieldValue('recordCalls', e.target.checked)
                      }}
                      labelOnRight
                      label={t('chat:callingSettings.recordAutomatically')}
                    />
                    <ToggleField
                      name="callTranscription"
                      value={values.recordCalls ? values.callTranscription : false}
                      onChange={(e) => setFieldValue('callTranscription', e.target.checked)}
                      disabled={!values.recordCalls}
                      labelOnRight
                      label={t('chat:callingSettings.callTranscription')}
                    />
                    <div className="toggle-row">
                      <ToggleField
                        name="voicemailEnabled"
                        value={values.voicemailEnabled}
                        onChange={(e) => setFieldValue('voicemailEnabled', e.target.checked)}
                        labelOnRight
                        label={t('chat:callingSettings.voicemail')}
                      />
                      <button
                        className="button button-black-outline"
                        onClick={() => setManageVoicemailVisible(true)}
                      >
                        <Gear size={20} fill="#646464" weight="fill" />{' '}
                        {t('chat:callingSettings.manageGreetings')}
                      </button>
                    </div>
                  </div>
                </div>
                {values.voicemailEnabled && (
                  <React.Fragment>
                    <div className="flex-row greeting-select-container">
                      <Filter
                        placeholder={getFilterPlaceholder(values.voicemailMessage)}
                        options={voicemailMessageOptions}
                        value={getFilterValue(values.voicemailMessage)}
                        disabled={isVoicemailLoading}
                        multiple={false}
                        name="voicemailMessage"
                        closeAfterSelect
                        label={t('chat:callingSettings.businessHoursGreeting')}
                        onChange={(e) => setFieldValue('voicemailMessage', e.target.value.value)}
                      />
                    </div>
                    <div className="flex-row">
                      <div className="standard-header">
                        {t('chat:callingSettings.ringDuration')}{' '}
                        <Info data-tip data-for="ring-duration" size={20} />
                      </div>
                      <SelectField
                        className="ring-duration"
                        name="ringDuration"
                        options={ringDurationOptions}
                        placeholder={t('chat:callingSettings.selectDuration')}
                        value={values.ringDuration}
                        onChange={handleChange}
                      />
                      <ReactTooltip
                        className="tooltip"
                        id="ring-duration"
                        place="top"
                        effect="solid"
                      >
                        <div>{t('chat:callingSettings.ringDurationTooltip')}</div>
                      </ReactTooltip>
                    </div>
                    <div className="flex-row greeting-select-container">
                      <Filter
                        placeholder={getFilterPlaceholder(values.outOfOfficeVoicemailMessage)}
                        options={voicemailMessageOptions}
                        value={getFilterValue(values.outOfOfficeVoicemailMessage)}
                        disabled={isVoicemailLoading}
                        multiple={false}
                        name="outOfOfficeVoicemailMessage"
                        closeAfterSelect
                        label={t('chat:callingSettings.outOfBusinessHoursGreeting')}
                        onChange={(e) =>
                          setFieldValue('outOfOfficeVoicemailMessage', e.target.value.value)
                        }
                      />
                    </div>
                    <div className="flex-row">
                      <div className="standard-header">
                        {t('chat:callingSettings.ringDuration')}{' '}
                        <Info data-tip data-for="out-ring-duration" size={20} />
                      </div>
                      <SelectField
                        className="ring-duration"
                        name="outOfOfficeRingDuration"
                        options={ringDurationOptions}
                        placeholder={
                          values.outOfOfficeInstantVoicemail
                            ? t('chat:callingSettings.zeroSec')
                            : t('chat:callingSettings.selectDuration')
                        }
                        value={
                          values.outOfOfficeInstantVoicemail ? 0 : values.outOfOfficeRingDuration
                        }
                        onChange={handleChange}
                        disabled={values.outOfOfficeInstantVoicemail}
                      />
                      <CheckboxField
                        name="outOfOfficeInstantVoicemail"
                        label={t('chat:callingSettings.immediatelyRedirect')}
                        value={values.outOfOfficeInstantVoicemail}
                        onChange={(e) => {
                          setFieldValue('outOfOfficeRingDuration', e.target.checked ? 0 : 25)
                          handleChange(e)
                        }}
                      />
                      <ReactTooltip
                        className="tooltip"
                        id="out-ring-duration"
                        place="top"
                        effect="solid"
                      >
                        <div>{t('chat:callingSettings.ringDurationTooltip')}</div>
                      </ReactTooltip>
                    </div>
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </Formik>
        ) : null}
      </div>
      {upsellingVisible && building && (
        <CallSettingsUpselling
          onClose={() => setUpsellingVisible(false)}
          buildingId={building.id}
        />
      )}
      {isManageVoicemailVisible && (
        <ManageVoicemailWindow
          onClose={() => setManageVoicemailVisible(false)}
          voicemailMessages={voicemailMessages}
          inboxId={inbox.id}
        />
      )}
    </React.Fragment>
  )
}

CallingSettings.propTypes = {
  building: PropTypes.object,
  inbox: PropTypes.object,
  voicemailMessages: PropTypes.array,
  getVoicemailMessages: PropTypes.func,
}

export default connect(
  (store) => ({
    voicemailMessages: selectVoicemailMessages(store),
  }),
  {
    getVoicemailMessages,
  },
)(CallingSettings)
